import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Spinner } from '@notino/react-styleguide'

// hooks
import useMessages from '../../hooks/useMessages'

// components
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden'

// styles
import * as SC from './OverlayLoadingStyles'

type Props = PropsWithChildren<{
	inertWhileLoading?: boolean
	isLoading?: boolean
	className?: string
	/** When true, announce Loading / Content loaded in a live region. Default false. */
	announceStatus?: boolean
}>

const OverlayLoading = (props: Props) => {
	const { isLoading = false, className, children, inertWhileLoading = true, announceStatus = false } = props
	const { messages } = useMessages()
	const [statusMessage, setStatusMessage] = useState('')
	const wasLoadingRef = useRef(false)

	useEffect(() => {
		if (!announceStatus) {
			return
		}

		if (isLoading) {
			setStatusMessage(messages.Loading)
			wasLoadingRef.current = true
			return
		}

		if (wasLoadingRef.current) {
			setStatusMessage(messages['Content loaded'])
			wasLoadingRef.current = false
		}
	}, [announceStatus, isLoading, messages])

	return (
		<SC.OverlayLoadingWrapper className={className} aria-busy={isLoading || undefined}>
			{announceStatus && (
				<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
					{statusMessage}
				</VisuallyHidden>
			)}
			{isLoading && (
				<SC.SpinnerWrapper aria-hidden>
					<Spinner size={24} color={'icon.primary'} />
				</SC.SpinnerWrapper>
			)}
			<SC.OverlayLoadingContent $isLoading={isLoading} {...(isLoading && inertWhileLoading ? { inert: '' } : undefined)}>
				{children}
			</SC.OverlayLoadingContent>
		</SC.OverlayLoadingWrapper>
	)
}

export default OverlayLoading
